<template>
  <div class="ui-content dashboard mark-attendance-page">
    <v-card class="mx-auto normal-card autohg">
      <!-- Filters -->
      <div class="user-data attendance-filters">
        <v-row>
          <v-col cols="12" sm="12">
            <div class="show-drop-down">
              <div class="label-data">{{ isReadOnlyFilters ? 'Date' : 'Select Date' }}</div>
              <div class="field-data">
                <v-text-field
                  v-if="isReadOnlyFilters"
                  :value="formattedDate"
                  outlined
                  dense
                  hide-details
                  readonly
                  disabled
                ></v-text-field>
                <template v-else>
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formattedDate"
                        placeholder="Select date"
                        outlined
                        dense
                        hide-details
                        readonly
                        v-on="on"
                        append-icon="mdi-calendar"
                        @click:append="dateMenu = true"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="selectedDate"
                      :max="today"
                      @input="onDateSelect"
                    ></v-date-picker>
                  </v-menu>
                </template>
              </div>
            </div>
            <div class="show-drop-down">
              <div class="label-data">{{ isReadOnlyFilters ? 'Center' : 'Select Center' }}</div>
              <div class="field-data">
                <v-text-field
                  v-if="isReadOnlyFilters"
                  :value="editCenterName"
                  outlined
                  dense
                  hide-details
                  readonly
                  disabled
                ></v-text-field>
                <v-select
                  v-else
                  v-model="selectedCenterId"
                  :items="centers"
                  item-text="text"
                  item-value="value"
                  placeholder="Select center"
                  outlined
                  dense
                  hide-details
                  @change="onCenterChange"
                ></v-select>
              </div>
            </div>
            <div class="show-drop-down">
              <div class="label-data">{{ isReadOnlyFilters ? 'Batch' : 'Select Batch' }}</div>
              <div class="field-data">
                <v-text-field
                  v-if="isReadOnlyFilters"
                  :value="editBatchName"
                  outlined
                  dense
                  hide-details
                  readonly
                  disabled
                ></v-text-field>
                <v-select
                  v-else
                  v-model="selectedBatchId"
                  :items="batches"
                  item-text="text"
                  item-value="value"
                  placeholder="Select batch"
                  outlined
                  dense
                  hide-details
                  :disabled="!centers.length"
                  @change="onBatchChange"
                ></v-select>
              </div>
            </div>
            <div class="show-drop-down">
              <div class="label-data">{{ isReadOnlyFilters ? 'Subject' : 'Select Subject' }}</div>
              <div class="field-data">
                <v-text-field
                  v-if="isReadOnlyFilters"
                  :value="editSubjectName"
                  outlined
                  dense
                  hide-details
                  readonly
                  disabled
                ></v-text-field>
                <v-select
                  v-else
                  v-model="selectedSubjectId"
                  :items="subjects"
                  item-text="text"
                  item-value="value"
                  placeholder="Select subject"
                  outlined
                  dense
                  hide-details
                  :disabled="!batches.length"
                  @change="onSubjectChange"
                ></v-select>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Submitted Banner -->
      <div v-if="isSubmitted && !isViewMode" class="attendance-status-banner">
        <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
        Attendance already submitted for this selection. You can update and
        re-submit.
      </div>

      <!-- Student Attendance Table -->
      <div v-if="students.length" class="attendance-toolbar">
        <!-- Search -->
        <div class="attendance-search">
          <v-text-field
            v-model="searchText"
            placeholder="Search by name or roll number (comma separated)"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <div class="attendance-actions-top">
          <div v-if="!isViewMode">
            <v-btn small class="mr-2 cust-btn" color="primary" @click="markAll(true)">
              Mark All Present
            </v-btn>
            <v-btn small class="cust-btn" color="primary" @click="markAll(false)">
              Mark All Absent
            </v-btn>
          </div>
          <div v-else></div>
          <div class="attendance-count">
            {{ presentCount }}/{{ students.length }} Present
          </div>
        </div>
      </div>

      <div class="attendance-table-section" v-if="students.length">
        <v-data-table
          :headers="headers"
          :items="students"
          :search="searchText"
          :custom-filter="studentFilter"
          class="elevation-2 cust-table"
          :mobile-breakpoint="0"
          hide-default-footer
          fixed-header
          :items-per-page="-1"
        >
          <template v-slot:header.roll_number="{ header }">
            <span style="color: blue">Roll No.</span> / Student ID
          </template>
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="(item, idx) in items" :key="item.student_id">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.name }}</td>
                <td>
                  <span v-if="item.roll_number" class="roll-number-text">{{ item.roll_number }}</span>
                  <span v-else>{{ item.student_id }}</span>
                </td>
                <td class="text-center">
                  <div class="attendance-toggle">
                    <v-switch
                      v-model="item.is_present"
                      :color="item.is_present ? 'green' : 'red'"
                      inset
                      hide-details
                      :disabled="isViewMode"
                      class="mt-0 pt-0"
                    ></v-switch>
                    <span
                      :class="[
                        'attendance-label',
                        item.is_present ? 'present-label' : 'absent-label',
                      ]"
                    >
                      {{ item.is_present ? "Present" : "Absent" }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>

        <!-- Submit Button -->
        <div v-if="!isViewMode" class="attendance-submit-section">
          <v-btn
            color="primary"
            class="ma-2 cust-btn"
            @click="openConfirmation"
          >
            {{ isSubmitted ? "Update Attendance" : "Submit Attendance" }}
          </v-btn>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!students.length && hasLoadedStudents"
        class="text-center pa-8"
      >
        <v-icon size="48" color="grey lighten-1">mdi-account-group</v-icon>
        <p class="mt-3 grey--text">
          No students found for the selected filters.
        </p>
      </div>

      <!-- Initial State -->
      <div
        v-else-if="!selectedCenterId || !selectedBatchId || !selectedSubjectId"
        class="text-center pa-8"
      >
        <v-icon size="48" color="grey lighten-1"
          >mdi-clipboard-check-outline</v-icon
        >
        <p class="mt-3 grey--text">
          Please select Center, Batch, Subject, and Date to view students.
        </p>
      </div>

      <!-- Confirmation Dialog -->
      <AlertBox
        :status="showConfirmation"
        :onClose="onConfirmationClose"
        :data="confirmationData"
        component="components/attendance/AttendanceConfirmation"
        width="500"
      />
    </v-card>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import constants from "@/utils/constants.js";
import AlertBox from "@/components/coordinator/Alert-Box.vue";

const user = JSON.parse(sessionStorage.getItem("user"));

export default {
  name: "MarkAttendance",
  components: { AlertBox },
  data() {
    return {
      centers: [],
      batches: [],
      subjects: [],
      students: [],

      selectedCenterId: null,
      selectedBatchId: null,
      selectedSubjectId: null,
      selectedDate: new Date().toISOString().substr(0, 10),
      today: new Date().toISOString().substr(0, 10),

      dateMenu: false,
      isSubmitted: false,
      hasLoadedStudents: false,
      searchText: "",

      editCenterName: "",
      editBatchName: "",
      editSubjectName: "",

      showConfirmation: false,
      confirmationData: null,

      headers: [
        { text: "#", value: "sno", sortable: false, width: "60px" },
        { text: "Name", value: "name", sortable: true },
        { text: "Roll No. / Student ID", value: "roll_number", sortable: true },
        {
          text: "Attendance",
          value: "is_present",
          sortable: false,
          align: "center",
          width: "180px",
        },
      ],
    };
  },
  computed: {
    isEditMode() {
      return !!this.$route.query.attendance_id && this.$route.query.mode !== "view";
    },
    isViewMode() {
      return this.$route.query.mode === "view";
    },
    isReadOnlyFilters() {
      return this.isEditMode || this.isViewMode;
    },
    formattedDate() {
      if (!this.selectedDate) return "";
      const parts = this.selectedDate.split("-");
      return parts[2] + "-" + parts[1] + "-" + parts[0];
    },
    presentCount() {
      return this.students.filter((s) => s.is_present).length;
    },
  },
  mounted() {
    if (this.isEditMode || this.isViewMode) {
      this.prefillFromQuery();
    } else {
      this.requestGetCenters();
    }
  },
  methods: {
    ...mapActions({
      getCenters: "centerAndBatchStore/getCenters",
      getCentersByUser: "centerAndBatchStore/getCentersByUser",
      getBatches: "centerAndBatchStore/getBatches",
      getBatchSubjects: "attendanceStore/getBatchSubjects",
      getAttendanceById: "attendanceStore/getAttendanceById",
      getStudents: "attendanceStore/getStudents",
      submitAttendance: "attendanceStore/submitAttendance",
    }),

    requestGetCenters() {
      this.$root.showLoader = true;
      const action =
        user && (Number(user.role_id) === constants.ROLES_ID.CO || Number(user.role_id) === constants.ROLES_ID.TR)
          ? "getCentersByUser"
          : "getCenters";

      this[action]()
        .then((response) => {
          this.$root.showLoader = false;
          if (response && response.length) {
            this.centers = response.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          } else if (response && response.list && response.list.length) {
            this.centers = response.list.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          }
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },

    prefillFromQuery() {
      const q = this.$route.query;
      if (!q.attendance_id) return;

      this.$root.showLoader = true;
      this.getAttendanceById({ attendance_id: Number(q.attendance_id) })
        .then((record) => {
          this.$root.showLoader = false;
          if (!record) return;

          // Set filters from the single response
          this.selectedDate = record.date.substr(0, 10);
          this.selectedCenterId = record.center_id;
          this.selectedBatchId = record.batch_id;
          this.selectedSubjectId = record.subject_id;
          this.editCenterName = record.center_name;
          this.editBatchName = record.batch_name;
          this.editSubjectName = record.subject_name;

          // Set students from the same response
          this.isSubmitted = record.is_submitted || false;
          this.hasLoadedStudents = true;
          this.students = (record.students || []).map((s) => ({
            student_id: s.student_id,
            name: s.name,
            email: s.email,
            username: s.username,
            roll_number: s.roll_number || "",
            attendance_detail_id: s.attendance_detail_id,
            is_present:
              s.attendance_detail_id != null ? s.is_present === 1 : true,
          }));
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },

    onCenterChange() {
      this.selectedBatchId = null;
      this.selectedSubjectId = null;
      this.batches = [];
      this.subjects = [];
      this.students = [];
      this.isSubmitted = false;
      this.hasLoadedStudents = false;

      if (!this.selectedCenterId) return;

      this.$root.showLoader = true;
      this.getBatches({ center_id: [this.selectedCenterId] })
        .then((response) => {
          this.$root.showLoader = false;
          if (response && response.list && response.list.length) {
            this.batches = response.list.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          }
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },

    onBatchChange() {
      this.selectedSubjectId = null;
      this.subjects = [];
      this.students = [];
      this.isSubmitted = false;
      this.hasLoadedStudents = false;

      if (!this.selectedBatchId) return;

      this.$root.showLoader = true;
      this.getBatchSubjects({ batch_id: this.selectedBatchId })
        .then((response) => {
          this.$root.showLoader = false;
          if (response && response.length) {
            this.subjects = response.map((x) => ({
              text: x.title,
              value: x.id,
            }));
          }
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },

    onSubjectChange() {
      this.loadStudents();
    },

    onDateSelect() {
      this.dateMenu = false;
      if (this.selectedBatchId && this.selectedSubjectId) {
        this.loadStudents();
      }
    },

    loadStudents() {
      if (
        !this.selectedBatchId ||
        !this.selectedSubjectId ||
        !this.selectedDate
      ) {
        return;
      }

      this.$root.showLoader = true;
      this.hasLoadedStudents = false;

      this.getStudents({
        batch_id: this.selectedBatchId,
        subject_id: this.selectedSubjectId,
        date: this.selectedDate,
      })
        .then((response) => {
          this.$root.showLoader = false;
          this.hasLoadedStudents = true;

          if (response && response.list) {
            this.isSubmitted = response.is_submitted || false;
            this.students = response.list.map((s) => ({
              student_id: s.student_id,
              name: s.name,
              email: s.email,
              username: s.username,
              roll_number: s.roll_number || "",
              attendance_detail_id: s.attendance_detail_id,
              is_present:
                s.attendance_detail_id != null ? s.is_present === 1 : true,
            }));
          } else {
            this.students = [];
          }
        })
        .catch(() => {
          this.$root.showLoader = false;
          this.hasLoadedStudents = true;
          this.students = [];
        });
    },

    studentFilter(value, search, item) {
      if (!search || !search.trim()) return true;
      const terms = search.split(",").map((t) => t.trim().toLowerCase()).filter((t) => t);
      if (!terms.length) return true;
      const name = (item.name || "").toLowerCase();
      const roll = String(item.roll_number || "").toLowerCase();
      const studentId = String(item.student_id || "").toLowerCase();
      const username = (item.username || "").toLowerCase();
      return terms.some((term) => name.includes(term) || roll.includes(term) || studentId.includes(term) || username.includes(term));
    },

    markAll(present) {
      this.students = this.students.map((s) => ({
        ...s,
        is_present: present,
      }));
    },

    openConfirmation() {
      if (!this.students.length) {
        this.$toasted.error("No students to submit attendance for", {
          icon: "info",
        });
        return;
      }

      const absentStudents = this.students
        .filter((s) => !s.is_present)
        .map((s) => ({ name: s.name, roll_number: s.roll_number, student_id: s.student_id }));

      this.confirmationData = {
        presentCount: this.presentCount,
        absentCount: this.students.length - this.presentCount,
        totalCount: this.students.length,
        absentStudents: absentStudents,
      };
      this.showConfirmation = true;
    },

    onConfirmationClose(result) {
      this.showConfirmation = result.status;
      if (result && result.action === "confirm") {
        this.doSubmitAttendance();
      }
    },

    doSubmitAttendance() {
      this.$root.showLoader = true;

      const attendance = this.students.map((s) => ({
        student_id: s.student_id,
        is_present: s.is_present,
      }));

      const payload = {
        attendance: attendance,
      };

      if (this.isEditMode) {
        payload.attendance_id = Number(this.$route.query.attendance_id);
      } else {
        payload.batch_id = this.selectedBatchId;
        payload.subject_id = this.selectedSubjectId;
        payload.date = this.selectedDate;
      }

      this.submitAttendance(payload)
        .then((response) => {
          this.$root.showLoader = false;
          this.isSubmitted = true;
          this.$toasted.success(
            (response && response.msg) || "Attendance submitted successfully",
            { icon: "check_circle" }
          );
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },
  },
};
</script>
<style>
.mark-attendance-page .normal-card.autohg {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100% !important;
}
.mark-attendance-page .attendance-filters {
  height: auto;
  min-height: 60px;
  padding: 10px 0;
  flex-shrink: 0;
}
.mark-attendance-page .attendance-filters .show-drop-down {
  min-width: 300px;
  margin-bottom: 8px;
}
.mark-attendance-page .attendance-filters .show-drop-down .field-data {
  min-width: 200px;
  flex: 1;
}
.mark-attendance-page .attendance-status-banner {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  margin: 8px 20px;
  border-radius: 4px;
  background-color: #e8f5e9;
  color: #2e7d32;
  font-size: 14px;
  clear: both;
}
.mark-attendance-page .attendance-status-banner {
  flex-shrink: 0;
}
.mark-attendance-page .attendance-toolbar {
  padding: 12px 20px 0;
  flex-shrink: 0;
}
.mark-attendance-page .attendance-table-section {
  padding: 0 20px 20px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.mark-attendance-page .attendance-search {
  margin-bottom: 12px;
  max-width: 500px;
}
.mark-attendance-page .attendance-actions-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}
.mark-attendance-page .roll-number-text {
  color: blue;
}
.mark-attendance-page .attendance-count {
  font-weight: 600;
  font-size: 14px;
  color: #555;
}
.mark-attendance-page .attendance-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
}
.mark-attendance-page .attendance-label {
  font-size: 12px;
  font-weight: 600;
  min-width: 50px;
}
.mark-attendance-page .present-label {
  color: #4caf50;
}
.mark-attendance-page .absent-label {
  color: #f44336;
}
.mark-attendance-page .attendance-submit-section {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
}
.mark-attendance-page .cust-table table {
  min-width: 500px;
}
.mark-attendance-page .cust-table thead.v-data-table-header th {
  background: #FAFAFA !important;
}
.mark-attendance-page .cust-table thead.v-data-table-header th.active,
.mark-attendance-page .cust-table thead.v-data-table-header th:hover {
  background: #FAFAFA !important;
  color: #000 !important;
}
@media all and (max-width: 991px) {
  .mark-attendance-page .attendance-filters .show-drop-down {
    min-width: 250px;
  }
}
@media all and (max-width: 600px) {
  .mark-attendance-page .attendance-filters .show-drop-down {
    float: none;
    min-width: 100%;
    justify-content: flex-start;
  }
  .mark-attendance-page .attendance-actions-top {
    flex-direction: column;
    align-items: flex-start;
  }
  .mark-attendance-page .attendance-table-section {
    padding: 12px 10px 20px;
  }
  .mark-attendance-page .attendance-status-banner {
    margin: 8px 10px;
    font-size: 13px;
  }
  .mark-attendance-page .attendance-search {
    max-width: 100%;
  }
}
</style>
